/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { memo, useMemo } from 'react';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import BoardIcon from '@atlaskit/icon/core/board';
import SprintIcon from '@atlaskit/icon/core/sprint';
import { xcss } from '@atlaskit/primitives/xcss';
import Box from '@atlaskit/primitives/box';
import { Bleed } from '@atlaskit/primitives/bleed';
import { token } from '@atlaskit/tokens';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { BOARD_TYPES } from './boardTypes.constants.tsx';
import { DropdownItemContainer } from './DropdownItemContainer.tsx';
import type { DropdownItem } from './DropdownItemContentProps.types.tsx';
import type { FlyoutMenuContentEntryPointProps } from './FlyoutMenuContentEntryPoint.entrypoint.types.tsx';

const boardCreationTitle = defineMessage({
	id: 'software-board-create.common.board-creation-title',
	defaultMessage: 'Create a board',
	description:
		'The title for both the board creation flyout menu that allows a user to pick between a creating a scrum or kanban board as well as the modal that opens after the user has made a selection',
});

const scrumBoardCreationHeader = defineMessage({
	id: 'software-board-create.common.scrum-board-creation-header',
	defaultMessage: 'Scrum',
	description: 'The header for the scrum board creation option in the board creation flyout menu.',
});

const scrumBoardCreationDescription = defineMessage({
	id: 'software-board-create.common.scrum-board-creation-description',
	defaultMessage: 'Sprint towards your team objectives with a board, backlog, and reports.',
	description:
		'The description for both the scrum board creation option in the board creation flyout menu and the modal that opens after the user has made a selection. In the modal, this appears under the boardCreationTitle',
});

const kanbanBoardCreationHeader = defineMessage({
	id: 'software-board-create.common.kanban-board-creation-header',
	defaultMessage: 'Kanban',
	description: 'The header for the kanban board creation option in the board creation flyout menu.',
});

const kanbanBoardCreationDescription = defineMessage({
	id: 'software-board-create.common.kanban-board-creation-description',
	defaultMessage: 'Manage a continuous delivery of work with a kanban board and reports.',
	description:
		'The description for both the kanban board creation option in the board creation flyout menu and the modal that opens after the user has made a selection. In the modal, this appears under the boardCreationTitle',
});

export const FlyoutContentContainer = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	return (
		<Box xcss={menuContainerStyles}>
			<Box xcss={headerContainerStyles}>
				<Box xcss={headerStyles}>{formatMessage(boardCreationTitle)}</Box>
			</Box>
			<Bleed block="space.050">{children}</Bleed>
		</Box>
	);
};

const BoardCreateFlyoutMenuContent = ({
	props: { projectId, projectAvatar, triggerRef },
}: {
	props: FlyoutMenuContentEntryPointProps;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const dropdownItems = useMemo(() => {
		const items: DropdownItem[] = [
			{
				boardType: BOARD_TYPES.SCRUM,
				title: formatMessage(scrumBoardCreationHeader),
				description: formatMessage(scrumBoardCreationDescription),
				icon: <SprintIcon label="" color={token('color.icon')} />,
			},
			{
				boardType: BOARD_TYPES.KANBAN,
				title: formatMessage(kanbanBoardCreationHeader),
				description: formatMessage(kanbanBoardCreationDescription),
				icon: <BoardIcon label="" color={token('color.icon')} />,
			},
		];

		return items.map(({ boardType, ...props }, index) => (
			<DropdownItemContainer
				key={boardType}
				entryPointProps={{ boardType, projectId, projectAvatar, triggerRef }}
				index={index}
				{...props}
			/>
		));
	}, [formatMessage, projectId, projectAvatar, triggerRef]);

	return (
		<FlyoutContentContainer>
			<DropdownItemGroup>{dropdownItems}</DropdownItemGroup>
		</FlyoutContentContainer>
	);
};

const MemoizedBoardCreateFlyoutMenuContent: React.MemoExoticComponent<
	typeof BoardCreateFlyoutMenuContent
> = memo(BoardCreateFlyoutMenuContent);

export default MemoizedBoardCreateFlyoutMenuContent;

const headerStyles = xcss({
	font: 'font.heading.xxsmall',
	color: 'color.text.subtlest',
});

const menuContainerStyles = xcss({
	width: '273px',
	paddingTop: 'space.100',
	paddingBottom: 'space.100',
});

const headerContainerStyles = xcss({
	height: 'space.200',
	padding: 'space.200',
	paddingTop: 'space.100',
	paddingBottom: 'space.100',
});
